import { 
  getAllRecordings, 
  getRecordingUrl,
  purchaseRecording,
  downloadRecording,
  renameRecording,
  deleteRecording,
  trimCropRecording,
  getProcessingStatus,
  monitorProcessingStatus
} from '../utils/recordingApi';

/**
 * Consolidated Recording Service
 * Provides a clean interface for all recording operations
 */
export class RecordingService {
  
  /**
   * Get recordings based on user role
   * Uses the role-aware backend endpoint
   */
  static async getRecordings(page = 0, size = 20) {
    try {
      // The main endpoint is now role-aware on the backend
      // ADMIN: gets all recordings
      // SITE_OWNER: gets owned recordings  
      // Regular users: get purchased recordings
      return await getAllRecordings(page, size);
    } catch (error) {
      console.error('RecordingService.getRecordings error:', error);
      throw error;
    }
  }

  /**
   * Purchase a recording
   * Handles both paid purchases and free grants for admins/owners
   */
  static async purchaseRecording(recordingId, discountCode = null) {
    try {
      const response = await purchaseRecording(recordingId, discountCode);
      
      // Check if this is a free grant (admin/owner)
      if (response.errorMessage === 'FREE_GRANTED') {
        return {
          success: true,
          isFreeGrant: true,
          message: 'Access granted! You can now watch this recording.'
        };
      }

      // Regular Stripe checkout
      if (response.sessionUrl) {
        return {
          success: true,
          isFreeGrant: false,
          checkoutUrl: response.sessionUrl,
          sessionId: response.sessionId
        };
      }

      // Error case
      return {
        success: false,
        error: response.errorMessage || 'Failed to create checkout session'
      };
    } catch (error) {
      console.error('RecordingService.purchaseRecording error:', error);
      return {
        success: false,
        error: error.message || 'Failed to initiate purchase'
      };
    }
  }

  /**
   * Get recording URL for viewing
   * Includes entitlement checking on backend
   */
  static async getRecordingUrl(livestreamId) {
    try {
      return await getRecordingUrl(livestreamId);
    } catch (error) {
      console.error('RecordingService.getRecordingUrl error:', error);
      throw error;
    }
  }

  /**
   * Download a recording
   * Includes entitlement checking on backend
   */
  static async downloadRecording(recordingId, filename = null) {
    try {
      return await downloadRecording(recordingId, filename);
    } catch (error) {
      console.error('RecordingService.downloadRecording error:', error);
      throw error;
    }
  }

  /**
   * Rename a recording
   * Only allowed for owners and admins
   */
  static async renameRecording(recordingId, newTitle) {
    try {
      return await renameRecording(recordingId, newTitle);
    } catch (error) {
      console.error('RecordingService.renameRecording error:', error);
      throw error;
    }
  }

  /**
   * Delete a recording
   * Only allowed for admins
   */
  static async deleteRecording(recordingId) {
    try {
      return await deleteRecording(recordingId);
    } catch (error) {
      console.error('RecordingService.deleteRecording error:', error);
      throw error;
    }
  }

  /**
   * Start trim/crop operation
   * Only allowed for admins and site owners
   */
  static async trimCropRecording(recordingId, trimCropData) {
    try {
      return await trimCropRecording(recordingId, trimCropData);
    } catch (error) {
      console.error('RecordingService.trimCropRecording error:', error);
      throw error;
    }
  }

  /**
   * Get processing status
   */
  static async getProcessingStatus(trackingId) {
    try {
      return await getProcessingStatus(trackingId);
    } catch (error) {
      console.error('RecordingService.getProcessingStatus error:', error);
      throw error;
    }
  }

  /**
   * Monitor processing status with polling
   */
  static async monitorProcessingStatus(trackingId, onStatusUpdate, maxAttempts = 60) {
    try {
      return await monitorProcessingStatus(trackingId, onStatusUpdate, maxAttempts);
    } catch (error) {
      console.error('RecordingService.monitorProcessingStatus error:', error);
      throw error;
    }
  }
}

export default RecordingService;
